import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { Account, AccountType, CreateAccountBody, UpdateAccountBody } from '@lira/shared';
import { apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { BankPicker, CategoryIcon, ColorSwatches, MoneyInput, Select, Sheet, Toggle } from '@/components/ui';
import { ACCOUNT_TYPE_OPTIONS } from './accountMeta';

interface AccountFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Conta a editar; ausente = criação. */
  account?: Account;
}

const DEFAULT_COLOR = '#6515DD';

interface FormState {
  name: string;
  type: AccountType;
  color: string;
  icon: string;
  initialBalanceCents: number;
  includeInTotal: boolean;
}

function initialState(account?: Account): FormState {
  return {
    name: account?.name ?? '',
    type: account?.type ?? 'checking',
    color: account?.color ?? DEFAULT_COLOR,
    icon: account?.icon ?? '',
    initialBalanceCents: account?.initialBalanceCents ?? 0,
    includeInTotal: account?.includeInTotal ?? true,
  };
}

/** Formulário de conta (1 passo, sem wizard). Cria ou edita conforme `account`. */
export function AccountFormModal({ open, onClose, account }: AccountFormModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => initialState(account));

  // resincroniza ao (re)abrir para outra conta
  useEffect(() => {
    if (open) setForm(initialState(account));
  }, [open, account]);

  const mutation = useMutation({
    mutationFn: (body: FormState) => {
      const payload: CreateAccountBody = {
        name: body.name.trim(),
        type: body.type,
        color: body.color,
        icon: body.icon,
        initialBalanceCents: body.initialBalanceCents,
        includeInTotal: body.includeInTotal,
      };
      if (account) {
        return apiPatch<{ account: Account }>(`/accounts/${account.id}`, payload as UpdateAccountBody);
      }
      return apiPost<{ account: Account }>('/accounts', payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      // A preferência includeInTotal altera diretamente o KPI "Saldo atual".
      // Sem invalidar o dashboard, o staleTime mantém a soma anterior em cache.
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
      onClose();
    },
  });

  const canSave = form.name.trim().length > 0 && !mutation.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate(form);
  };

  return (
    <Sheet open={open} onClose={onClose} title={account ? 'Editar conta' : 'Nova conta'}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="rounded-lg bg-canvas px-4 py-4">
          <label className="mb-1 block text-[13px] text-muted">Saldo inicial</label>
          <MoneyInput
            valueCents={form.initialBalanceCents}
            onChange={(cents) => setForm((f) => ({ ...f, initialBalanceCents: cents }))}
            accent="primary"
            size="lg"
            aria-label="Saldo inicial"
          />
        </div>

        <div>
          <label className="mb-1 block text-[13px] text-muted">Tipo de conta</label>
          <Select<AccountType>
            value={form.type}
            options={ACCOUNT_TYPE_OPTIONS}
            onChange={(type) => setForm((f) => ({ ...f, type }))}
            aria-label="Tipo de conta"
          />
        </div>

        <div>
          <label htmlFor="account-name" className="mb-1 block text-[13px] text-muted">
            Descrição
          </label>
          <input
            id="account-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            maxLength={60}
            placeholder="Ex.: Nubank, Carteira, Poupança"
            autoComplete="off"
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
          />
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <CategoryIcon icon={form.icon} color={form.color} size={44} fallbackText={form.name} />
            <label className="block text-[13px] text-muted">Banco / ícone</label>
          </div>
          <BankPicker
            value={form.icon}
            onChange={(icon, brandColor) =>
              setForm((f) => ({ ...f, icon, color: brandColor ?? f.color }))
            }
          />
        </div>

        <div>
          <label className="mb-2 block text-[13px] text-muted">Cor da conta</label>
          <ColorSwatches
            value={form.color}
            onChange={(color) => setForm((f) => ({ ...f, color }))}
          />
        </div>

        <Toggle
          checked={form.includeInTotal}
          onChange={(includeInTotal) => setForm((f) => ({ ...f, includeInTotal }))}
          label="Incluir na soma da tela inicial"
          accent="primary"
        />

        {mutation.isError && <p className="text-sm text-expense">{errorMessage(mutation.error)}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
